import React, { useEffect, useState, useRef } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { DatabaseBackup, Download, Upload, Mail, Save, AlertTriangle, Clock, CheckCircle2, Image as ImageIcon, ChevronDown, HardDrive } from "lucide-react";
import { useUiState } from "@/lib/uiState";

const KIND_LABELS = { download: "Full download", "download-dbonly": "DB-only download", email: "Emailed", scheduled: "Scheduled email" };
const KIND_STYLES = {
  download: "bg-primary/10 text-primary",
  "download-dbonly": "bg-sky-100 text-sky-800",
  email: "bg-emerald-100 text-emerald-800",
  scheduled: "bg-amber-100 text-amber-800",
};

const Section = ({ title, subtitle, icon: Icon, danger, open, onToggle, testId, children }) => (
  <div data-testid={testId} className={`bg-card border ${danger ? "border-destructive/40" : "border-border"} rounded-lg mb-5`}>
    <button type="button" onClick={onToggle} data-testid={testId ? `${testId}-toggle-header` : undefined}
      className="w-full flex items-start justify-between gap-3 p-6 text-left">
      <div>
        <h2 className={`font-serif text-xl flex items-center gap-2 ${danger ? "text-destructive" : ""}`}>
          {Icon && <Icon className="h-5 w-5" />} {title}
        </h2>
        {subtitle && open && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
      </div>
      <ChevronDown className={`h-5 w-5 text-muted-foreground shrink-0 mt-1 transition-transform ${open ? "" : "-rotate-90"}`} />
    </button>
    {open && <div className="px-6 pb-6">{children}</div>}
  </div>
);

export default function Backup() {
  const [cfg, setCfg] = useState({ enabled: false, recipient_email: "", last_sent_at: null, last_status: null });
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");
  const [history, setHistory] = useState([]);
  const [open, setOpen] = useUiState("ud_backup_sections", { storage: true, backup: true, schedule: true, restore: true, history: true });
  const [optProgress, setOptProgress] = useState(null);
  const [storage, setStorage] = useState(null);
  const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));
  const fileRef = useRef();

  const load = () => api.get("/admin/backup/settings").then((r) => setCfg(r.data)).catch(() => {});
  const loadHistory = () => api.get("/admin/backup/history").then((r) => setHistory(r.data.history || [])).catch(() => {});
  const loadStorage = () => api.get("/admin/storage-stats").then((r) => setStorage(r.data)).catch(() => {});
  useEffect(() => { load(); loadHistory(); loadStorage(); api.get("/admin/optimize-images/status").then((r) => { if (r.data?.running) { setBusy("optimize"); setOptProgress(r.data); pollOptimize(); } }).catch(() => {}); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/backup/settings", { enabled: cfg.enabled, recipient_email: cfg.recipient_email });
      setCfg(r.data);
      toast.success("Backup settings saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const download = async (withImages = true) => {
    setBusy(withImages ? "download" : "download-dbonly");
    try {
      const { data } = await api.post("/admin/backup/download-token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/backup/download?token=${encodeURIComponent(data.token)}&images=${withImages ? 1 : 0}`;
      // Native browser download — streams straight to disk (no big blob in JS memory).
      const a = document.createElement("a");
      a.href = url;
      a.rel = "noopener";
      document.body.appendChild(a); a.click(); a.remove();
      toast.success(withImages ? "Full backup download started" : "DB-only backup download started");
      setTimeout(loadHistory, 1500);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Download failed"); }
    finally { setBusy(""); }
  };

  const pollOptimize = async () => {
    try {
      const { data } = await api.get("/admin/optimize-images/status");
      setOptProgress(data);
      if (data.running) { setTimeout(pollOptimize, 1500); }
      else {
        setBusy("");
        if (data.done) toast.success(data.message || "Optimization complete", { duration: 8000 });
        loadStorage();
        setTimeout(() => setOptProgress(null), 10000);
      }
    } catch { setBusy(""); }
  };

  const optimizeImages = async () => {
    if (!window.confirm("Recompress all existing photos to shrink storage and backups? This runs in the background — you can keep working while it processes. Images are downscaled to at most 2000px and only replaced when smaller.")) return;
    setBusy("optimize");
    try {
      await api.post("/admin/optimize-images");
      pollOptimize();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not start optimization"); setBusy(""); }
  };


  const emailNow = async () => {
    setBusy("email");
    try {
      const r = await api.post("/admin/backup/send-now");
      toast.success(`Backup emailed to ${r.data.recipient} (${r.data.records} records)`);
      load(); loadHistory();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const restore = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!window.confirm("Restore will WIPE all current data and replace it with the contents of this backup file. This cannot be undone. Continue?")) return;
    setBusy("restore");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/backup/restore", fd);
      toast.success(`Restored ${r.data.total} records across ${Object.keys(r.data.collections).length} collections`);
      load();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };
  const fmtSize = (b) => {
    if (!b && b !== 0) return "—";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
  };
  const baseName = (p) => (p || "").split("/").pop();

  return (
    <div data-testid="backup-page" className="max-w-3xl">
      <div className="mb-6">
        <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-muted-foreground">Administration</div>
        <h1 className="font-serif text-4xl tracking-tight flex items-center gap-3 mt-1">
          <DatabaseBackup className="h-8 w-8 text-primary" /> Backup &amp; Restore
        </h1>
        <p className="text-muted-foreground text-sm mt-2 max-w-xl">
          Export a complete snapshot of the alumni database, restore from a saved snapshot, or have a full backup
          emailed to your superadmin automatically every week.
        </p>
      </div>

      {/* Storage usage meter */}
      <Section title="Storage usage" icon={HardDrive} testId="storage-meter-card" open={open.storage} onToggle={() => toggle("storage")}
        subtitle="How much space your uploaded images and files take up in the database — spot bloat at a glance.">
        {!storage ? (
          <p className="text-sm text-muted-foreground italic">Loading storage usage…</p>
        ) : (
          <div data-testid="storage-stats">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
              <div className="bg-muted/40 rounded-lg p-4">
                <div className="text-2xl font-serif font-bold tabular-nums" data-testid="storage-total">{fmtSize(storage.total_bytes)}</div>
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mt-1">Total stored</div>
              </div>
              <div className="bg-muted/40 rounded-lg p-4">
                <div className="text-2xl font-serif font-bold tabular-nums">{storage.image_count}</div>
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mt-1">Images</div>
              </div>
              <div className="bg-muted/40 rounded-lg p-4">
                <div className="text-2xl font-serif font-bold tabular-nums">{storage.file_count}</div>
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mt-1">Total files</div>
              </div>
            </div>
            {storage.biggest?.length > 0 && (
              <>
                <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">Biggest files</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="storage-biggest-table">
                    <tbody>
                      {storage.biggest.map((f, i) => {
                        const pct = storage.biggest[0]?.size ? Math.round((f.size / storage.biggest[0].size) * 100) : 0;
                        return (
                          <tr key={f.path || i} data-testid="storage-biggest-row" className="border-b border-border/50 last:border-0">
                            <td className="py-2 pr-3 w-1/2">
                              <div className="font-medium truncate">{f.label}</div>
                              <div className="text-xs text-muted-foreground truncate">{baseName(f.path)}</div>
                            </td>
                            <td className="py-2 pr-3">
                              <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                                <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                              </div>
                            </td>
                            <td className="py-2 pl-2 text-right tabular-nums whitespace-nowrap">{fmtSize(f.size)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  Large photo libraries inflate backups. Use <strong>Optimize existing photos</strong> below to recompress images and shrink storage.
                </p>
              </>
            )}
          </div>
        )}
      </Section>

      {/* Manual backup */}
      <Section title="Backup now" icon={DatabaseBackup} open={open.backup} onToggle={() => toggle("backup")}
        subtitle="Download a full snapshot (with images), a lightweight DB-only snapshot, or email it to your scheduled recipient immediately.">
        <div className="flex flex-wrap gap-3">
          <button data-testid="backup-download-btn" onClick={() => download(true)} disabled={busy === "download"}
            className="flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-60">
            <Download className="h-4 w-4" /> {busy === "download" ? "Preparing…" : "Download full backup (with images)"}
          </button>
          <button data-testid="backup-download-dbonly-btn" onClick={() => download(false)} disabled={busy === "download-dbonly"}
            className="flex items-center gap-2 px-4 py-2.5 rounded-md border border-border text-sm font-medium hover:bg-muted disabled:opacity-60">
            <Download className="h-4 w-4" /> {busy === "download-dbonly" ? "Preparing…" : "Download DB-only (no images)"}
          </button>
          <button data-testid="backup-email-now-btn" onClick={emailNow} disabled={busy === "email"}
            className="flex items-center gap-2 px-4 py-2.5 rounded-md border border-border text-sm font-medium hover:bg-muted disabled:opacity-60">
            <Mail className="h-4 w-4" /> {busy === "email" ? "Sending…" : "Email backup now"}
          </button>
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          <strong>Full</strong> includes all uploaded images — use it when moving data to a new environment. <strong>DB-only</strong> is small and fast for a quick records snapshot when images already exist in the target database.
        </p>
        <div className="mt-4 pt-4 border-t border-border">
          <button data-testid="optimize-images-btn" onClick={optimizeImages} disabled={busy === "optimize"}
            className="flex items-center gap-2 px-4 py-2.5 rounded-md border border-border text-sm font-medium hover:bg-muted disabled:opacity-60">
            <ImageIcon className="h-4 w-4" /> {busy === "optimize" ? (optProgress?.total ? `Optimizing… ${optProgress.processed}/${optProgress.total}` : "Starting…") : "Optimize existing photos"}
          </button>
          {optProgress && (optProgress.running || optProgress.done) && (
            <div data-testid="optimize-progress" className="mt-2">
              {optProgress.total > 0 && (
                <div className="h-1.5 w-full max-w-md rounded-full bg-muted overflow-hidden">
                  <div className="h-full bg-primary transition-all" style={{ width: `${Math.min(100, Math.round((optProgress.processed / optProgress.total) * 100))}%` }} />
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                {optProgress.running
                  ? `Processing ${optProgress.processed} of ${optProgress.total} — ${optProgress.optimized} shrunk so far`
                  : optProgress.message}
              </p>
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-2">Shrinks photos uploaded before auto-optimization was added (downscales to ≤2000px, keeps format). Runs in the background — safe to run anytime; only replaces a file when it gets smaller.</p>
        </div>
      </Section>

      {/* Scheduled weekly backup */}
      <Section title="Weekly scheduled backup" open={open.schedule} onToggle={() => toggle("schedule")}
        subtitle={<>When enabled, a full backup is emailed to the address below every <b>Monday at 9:00 AM (US Central)</b>.</>}>
        <label className="flex items-center gap-3 mb-4 cursor-pointer select-none">
          <button
            type="button"
            data-testid="backup-toggle"
            onClick={() => setCfg((c) => ({ ...c, enabled: !c.enabled }))}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${cfg.enabled ? "bg-primary" : "bg-muted-foreground/30"}`}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${cfg.enabled ? "translate-x-5" : "translate-x-0.5"}`} />
          </button>
          <span className="text-sm font-medium">{cfg.enabled ? "Weekly backups ON" : "Weekly backups OFF"}</span>
        </label>

        <label className="block text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1">Send backups to (superadmin email)</label>
        <input
          data-testid="backup-recipient-input"
          type="email"
          value={cfg.recipient_email || ""}
          onChange={(e) => setCfg((c) => ({ ...c, recipient_email: e.target.value }))}
          placeholder="superadmin@udallas.edu"
          className="w-full max-w-md px-3 py-2.5 rounded-md border border-input bg-card focus:ring-2 focus:ring-accent focus:outline-none mb-4"
        />

        <div>
          <button data-testid="backup-save-btn" onClick={save} disabled={saving}
            className="flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-60">
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save schedule settings"}
          </button>
        </div>

        {(cfg.last_sent_at || cfg.last_status) && (
          <div data-testid="backup-last-status" className="mt-4 flex items-center gap-2 text-xs text-muted-foreground border-t border-border pt-3">
            <Clock className="h-3.5 w-3.5" />
            Last backup email: {cfg.last_sent_at ? fmt(cfg.last_sent_at) : "—"}
            {cfg.last_status && <span className="ml-1 inline-flex items-center gap-1 text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" /> {cfg.last_status}</span>}
          </div>
        )}
      </Section>

      {/* Restore */}
      <Section title={<><AlertTriangle className="h-5 w-5" /> Restore from backup</>} danger open={open.restore} onToggle={() => toggle("restore")}
        subtitle={<>Uploading a backup will <b>wipe all current data</b> and replace it with the contents of the file (full replace). This cannot be undone — download a fresh backup first.</>}>
        <input ref={fileRef} data-testid="backup-restore-input" type="file" accept="application/json,.json,application/gzip,.gz" onChange={restore} className="hidden" />
        <button data-testid="backup-restore-btn" onClick={() => fileRef.current?.click()} disabled={busy === "restore"}
          className="flex items-center gap-2 px-4 py-2.5 rounded-md border border-destructive text-destructive text-sm font-medium hover:bg-destructive/10 disabled:opacity-60">
          <Upload className="h-4 w-4" /> {busy === "restore" ? "Restoring…" : "Choose backup file to restore…"}
        </button>
      </Section>

      {/* Backup history (bottom) */}
      <Section title="Recent backups" testId="backup-history-card" open={open.history} onToggle={() => toggle("history")}
        subtitle="The last 20 snapshots created from this page (downloads and emails).">
        {history.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">No backups yet — create one above.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="backup-history-table">
              <thead className="text-muted-foreground border-b border-border">
                <tr className="text-left">
                  <th className="py-2 pr-4 font-medium">When</th>
                  <th className="py-2 pr-4 font-medium">Type</th>
                  <th className="py-2 pr-4 font-medium">Size</th>
                  <th className="py-2 pr-4 font-medium">Records</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h, i) => (
                  <tr key={h.id || i} data-testid="backup-history-row" className="border-b border-border/50 last:border-0">
                    <td className="py-2 pr-4 tabular-nums">{fmt(h.at)}</td>
                    <td className="py-2 pr-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${KIND_STYLES[h.kind] || "bg-muted text-muted-foreground"}`}>
                        {KIND_LABELS[h.kind] || h.kind}{h.includes_images ? "" : " · no images"}
                      </span>
                    </td>
                    <td className="py-2 pr-4 tabular-nums">{fmtSize(h.size_bytes)}</td>
                    <td className="py-2 pr-4 tabular-nums">{h.total_documents}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}
